// Payment Service for Marathi Vadhu Var Suchak
// Handles all payment-related API calls and gateway integrations

import { normalizePackage, sortActivePackages, type ActivePackage } from '@/types/package';

export type { ActivePackage } from '@/types/package';

export type CashfreePaymentChannel = 'upi' | 'card' | 'netbanking';

export type InitiatePaymentOptions = {
  cashfreeChannel?: CashfreePaymentChannel;
};

/** Cashfree order `payment_methods` filter codes (backend may use when creating CF order). */
export function getCashfreePaymentMethodsFilter(
  channel: CashfreePaymentChannel
): string {
  switch (channel) {
    case 'upi':
      return 'upi';
    case 'card':
      return 'dc,cc';
    case 'netbanking':
      return 'nb';
    default:
      return 'upi';
  }
}

export interface PackageData {
  id: string;
  name: string;
  description: string;
  durationMonths: number;
  price: number;
  currency: string;
  usdPrice?: number;
  eurPrice?: number;
  gbpPrice?: number;
  features: {
    mobileNumberAccessCount: number;
    sendMessageCount: number;
    memberChat: boolean;
    viewHoroscope: boolean;
    chatUsersCount: number;
    horoscopeCount: number;
    isAudioCalling: boolean;
    isVideoCalling: boolean;
    audioCallingMins: number;
    videoCallingMins: number;
    personalRelationshipManager: boolean;
    expertProfileWritingService: boolean;
    instantQuickCallSupport: boolean;
    assistedDashboard: boolean;
  };
}

export interface PaymentMethods {
  paymentMethods: string[];
  supportedCurrencies: string[];
  currencyRates: Record<string, number>;
}

export interface CouponData {
  id: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  discountAmount: number;
  finalAmount: number;
}

export interface CouponValidationResponse {
  isValid: boolean;
  message: string;
  coupon: CouponData;
}

export interface PaymentInitiationData {
  transactionId: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  gatewayOrderId: string;
  paymentData: Record<string, unknown>; // Gateway-specific data
}

export interface PaymentVerificationData {
  transactionId: string;
  status: string;
  amount: number;
  currency: string;
  package: {
    id: string;
    name: string;
    durationMonths: number;
  };
  userPackage: {
    id: string;
    startDate: string;
    endDate: string;
  };
  paypalCaptureId?: string;
}

export interface TransactionData {
  id: string;
  amount: number;
  discount: number;
  finalAmount: number;
  currency: string;
  paymentMethod: string;
  paymentStatus: string;
  paymentGatewayOrderId: string;
  paymentGatewayTransactionId: string;
  paymentGatewayPaymentId: string;
  razorpaySignature?: string;
  paypalOrderId?: string;
  paypalCaptureId?: string;
  failureReason?: string;
  refundAmount?: number;
  refundReason?: string;
  refundedAt?: string;
  createdAt: string;
  updatedAt: string;
  savings: number;
  savingsPercentage: number;
}

export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  memberId: string;
}

export interface TransactionDetailsResponse {
  status: string;
  transaction: TransactionData;
  user: UserData;
  package: PackageData;
  coupon: CouponData | null;
}

export interface PremiumStatusData {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    memberId: string;
    accountStatus: string;
    emailVerified: boolean;
    phoneVerified: boolean;
    profileVerified: string;
    subscriptionPlan: string;
    lastLoginAt: string;
    createdAt: string;
  };
  premium: {
    isPremium: boolean;
    hasActiveSubscription: boolean;
    subscription: {
      id: string;
      status: string;
      startDate: string;
      endDate: string;
      autoRenewal: boolean;
      paymentMethod: string;
      lastPaymentDate: string;
      nextBillingDate: string;
      package: PackageData;
    };
    features: {
      mobileNumberAccessCount: number;
      sendMessageCount: number;
      memberChat: boolean;
      viewHoroscope: boolean;
      personalRelationshipManager: boolean;
      expertProfileWritingService: boolean;
      instantQuickCallSupport: boolean;
      assistedDashboard: boolean;
      chatUsersCount: number;
      horoscopeCount: number;
      isAudioCalling: boolean;
      isVideoCalling: boolean;
      audioCallingMins: number;
      videoCallingMins: number;
    };
    daysRemaining: number;
    isExpired: boolean;
  };
  context: {
    canAccessMobileNumbers: boolean;
    canSendMessages: boolean;
    canViewHoroscope: boolean;
    canUseMemberChat: boolean;
    canUseAudioCalling: boolean;
    canUseVideoCalling: boolean;
    hasPersonalRelationshipManager: boolean;
    hasExpertProfileWriting: boolean;
    hasInstantSupport: boolean;
    hasAssistedDashboard: boolean;
  };
}

export interface PremiumStatusResponse {
  status: string;
  message: string;
  data: PremiumStatusData;
}

export interface SubscriptionData {
  status: string;
  subscription: {
    id: string;
    status: string;
    startDate: string;
    endDate: string;
    autoRenewal: boolean;
    paymentMethod: string;
    lastPaymentDate: string;
    nextBillingDate: string;
    package: PackageData;
  };
  isExpired: boolean;
  daysRemaining: number;
}

// API Response interfaces
interface ApiResponse<T> {
  status: string;
  message?: string;
  data: T;
}

interface ErrorResponse {
  status: string;
  message: string;
  error: {
    code: string;
    details: string;
  };
}

export interface TransactionHistory {
  id: string;
  userId: string;
  packageId: string;
  couponId: string | null;
  amount: number;
  discount: number;
  finalAmount: number;
  currency: string;
  paymentMethod: string;
  paymentStatus: string;
  paymentGatewayTransactionId: string | null;
  paymentGatewayOrderId: string | null;
  paymentGatewayPaymentId: string | null;
  razorpaySignature: string | null;
  paypalOrderId: string | null;
  paypalCaptureId: string | null;
  failureReason: string | null;
  refundAmount: number | null;
  refundReason: string | null;
  refundedAt: string | null;
  createdAt: string;
  updatedAt: string;
  package: {
    id: string;
    name: string;
    description: string;
    durationMonths: number;
    price: number;
    offerPrice: number;
    nriPrice: number;
    currency: string;
    mobileNumberAccessCount: number;
    sendMessageCount: number;
    memberChat: boolean;
    viewHoroscope: boolean;
    personalRelationshipManager: boolean;
    expertProfileWritingService: boolean;
    instantQuickCallSupport: boolean;
    assistedDashboard: boolean;
    chatUsersCount: number;
    horoscopeCount: number;
    isAudioCalling: boolean;
    isVideoCalling: boolean;
    audioCallingMins: number;
    videoCallingMins: number;
  };
  coupon: {
    id: string;
    code: string;
    discountAmount: number;
    discountType: string;
  } | null;
  savings: number;
  savingsPercentage: number;
  isRefunded: boolean;
  refundPercentage: number;
}

export interface PaymentHistoryResponse {
  status: string;
  message: string;
  data: {
    transactions: TransactionHistory[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
    summary: {
      totalTransactions: number;
      totalSpent: number;
      totalSavings: number;
      completedTransactions: number;
      pendingTransactions: number;
      failedTransactions: number;
      refundedTransactions: number;
    };
  };
}

class PaymentService {
  private baseURL: string;
  private accessToken: string | null = null;

  constructor() {
    this.baseURL = process.env.NEXT_PUBLIC_API_BASE_URL || '';
    this.accessToken = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  }

  private getAuthHeaders(requireAuth: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Always re-read latest token from localStorage in browser.
    // Checkout can stay open for a while; token may rotate in background.
    if (typeof window !== 'undefined') {
      this.accessToken = localStorage.getItem('accessToken');
    }

    if (requireAuth) {
      if (!this.accessToken) {
        throw new Error('No access token found. Please log in again.');
      }
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    } else if (this.accessToken) {
      // Include auth token if available, but don't require it
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    return headers;
  }

  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {},
    requireAuth: boolean = true
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...this.getAuthHeaders(requireAuth),
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorData: ErrorResponse = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: ApiResponse<T> = await response.json();
      return data.data;
    } catch (error) {
      // Payment API Error
      throw error;
    }
  }

  // Phase 1: Get Package Details
  async getPackageDetails(packageId: string): Promise<ActivePackage> {
    const data = await this.makeRequest<Record<string, unknown>>(`/packages/${packageId}`);
    return normalizePackage(data);
  }

  // Get active packages for membership page
  async getActivePackages(page = 1, limit = 10): Promise<ActivePackage[]> {
    const data = await this.makeRequest<{ packages?: Record<string, unknown>[] }>(
      `/packages/active?page=${page}&limit=${limit}`,
      {},
      false
    );

    const packagesRaw = data.packages ?? [];
    return sortActivePackages(
      packagesRaw
        .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
        .map((item) => normalizePackage(item))
    );
  }

  // Phase 1: Get Available Payment Methods
  async getPaymentMethods(): Promise<PaymentMethods> {
    return this.makeRequest<PaymentMethods>('/payment/methods');
  }

  // Phase 2: Validate Coupon
  async validateCoupon(couponCode: string, packageId: string, amount: number): Promise<CouponValidationResponse> {
    return this.makeRequest<CouponValidationResponse>('/coupons/validate', {
      method: 'POST',
      body: JSON.stringify({
        code: couponCode,
        packageId,
        orderAmount: amount,
      }),
    });
  }

  // Phase 3: Initiate Payment
  async initiatePayment(
    packageId: string,
    paymentMethod: 'razorpay' | 'paypal' | 'cashfree',
    currency: string,
    couponCode?: string,
    options?: InitiatePaymentOptions
  ): Promise<PaymentInitiationData> {
    const body: Record<string, unknown> = {
      packageId,
      paymentMethod,
      currency,
      couponCode,
    };

    if (paymentMethod === 'cashfree' && options?.cashfreeChannel) {
      body.cashfreeChannel = options.cashfreeChannel;
      body.cashfreePaymentMethods = getCashfreePaymentMethodsFilter(
        options.cashfreeChannel
      );
    }

    return this.makeRequest<PaymentInitiationData>('/payment/initiate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Phase 5: Verify Payment
  async verifyPayment(
    transactionId: string,
    paymentData: Record<string, unknown>
  ): Promise<PaymentVerificationData> {
    return this.makeRequest<PaymentVerificationData>('/payment/verify', {
      method: 'POST',
      body: JSON.stringify({
        transactionId,
        paymentData,
      }),
    });
  }

  // Phase 6: Get Transaction Details
  // Note: This can work without authentication for PayPal callbacks
  async getTransactionDetails(transactionId: string, requireAuth: boolean = true): Promise<TransactionDetailsResponse> {
    return this.makeRequest<TransactionDetailsResponse>(`/payment/transaction/${transactionId}`, {}, requireAuth);
  }

  // Phase 7: Get Premium Status
  async getPremiumStatus(): Promise<PremiumStatusResponse> {
    const url = `${this.baseURL}/payment/premium-status`;
    
    try {
      const response = await fetch(url, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData: ErrorResponse = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: PremiumStatusResponse = await response.json();
      // Premium status API response
      return data;
    } catch (error) {
      // Premium Status API Error
      throw error;
    }
  }

  // Phase 8: Get Subscription Status
  async getSubscriptionStatus(): Promise<SubscriptionData> {
    return this.makeRequest<SubscriptionData>('/payment/subscription');
  }

  // Phase 9: Get Payment History
  async getPaymentHistory(): Promise<PaymentHistoryResponse> {
    const url = `${this.baseURL}/payment/history`;
    
    try {
      const response = await fetch(url, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData: ErrorResponse = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data: PaymentHistoryResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Payment History API Error:', error);
      throw error;
    }
  }

  // Razorpay Integration
  async openRazorpayPayment(paymentData: {
    razorpay_key: string;
    amount: number;
    currency: string;
    order_id?: string;
    id?: string;
    name: string;
    description: string;
    prefill: {
      name: string;
      email: string;
      contact: string;
    };
  }): Promise<{
    razorpay_payment_id: string;
    razorpay_order_id: string;
    razorpay_signature: string;
  }> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Razorpay can only be used in browser environment'));
        return;
      }

      // Check if Razorpay is already loaded
      if ((window as unknown as { Razorpay?: unknown }).Razorpay) {
        this.openRazorpayModal(paymentData, resolve, reject);
        return;
      }

      // Load Razorpay script if not already loaded
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        this.openRazorpayModal(paymentData, resolve, reject);
      };
      script.onerror = () => {
        console.error('Failed to load Razorpay script');
        reject(new Error('Failed to load Razorpay script'));
      };
      document.head.appendChild(script);
    });
  }

  private openRazorpayModal(
    paymentData: {
      razorpay_key: string;
      amount: number;
      currency: string;
      order_id?: string;
      id?: string;
      name: string;
      description: string;
      prefill: {
        name: string;
        email: string;
        contact: string;
      };
    },
    resolve: (value: {
      razorpay_payment_id: string;
      razorpay_order_id: string;
      razorpay_signature: string;
    }) => void,
    reject: (reason?: unknown) => void
  ) {
    try {
      // Some backend responses provide Razorpay order id as "id" (order object),
      // while others provide "order_id". Support both.
      const gatewayOrderId = paymentData.order_id || paymentData.id;
      if (!gatewayOrderId) {
        throw new Error('Missing Razorpay order id in payment data');
      }

      const options = {
        key: paymentData.razorpay_key,
        currency: paymentData.currency,
        name: 'Marathi Vadhu Var Suchak',
        description: 'Membership Package Payment',
        order_id: gatewayOrderId,
        prefill: {
          name: paymentData.prefill?.name || '',
          email: paymentData.prefill?.email || '',
          contact: paymentData.prefill?.contact || ''
        },
        theme: {
          color: '#3B82F6'
        },
        handler: function (response: {
          razorpay_payment_id: string;
          razorpay_order_id: string;
          razorpay_signature: string;
        }) {
          resolve(response);
        },
        modal: {
          ondismiss: function () {
            reject(new Error('Payment cancelled by user'));
          }
        }
      };

      const rzp = new ((window as unknown as { Razorpay: new (options: unknown) => { open: () => void } }).Razorpay)(options);
      rzp.open();
    } catch (error) {
      console.error('Error opening Razorpay modal:', error);
      reject(error);
    }
  }

  private getCashfreeMode(): 'sandbox' | 'production' {
    const mode = process.env.NEXT_PUBLIC_CASHFREE_MODE?.toLowerCase();
    if (mode === 'sandbox' || mode === 'production') {
      return mode;
    }
    return process.env.NEXT_PUBLIC_APP_ENV === 'production' ? 'production' : 'sandbox';
  }

  private buildCashfreeCheckoutOptions(
    paymentSessionId: string,
    channel?: CashfreePaymentChannel
  ): Record<string, unknown> {
    const options: Record<string, unknown> = {
      paymentSessionId,
      redirectTarget: '_modal',
    };

    if (!channel) {
      return options;
    }

    // Best-effort hints for Cashfree hosted checkout (ignored safely if unsupported).
    const channelHints: Record<CashfreePaymentChannel, Record<string, unknown>> = {
      upi: { components: ['upi'], defaultPaymentMethod: 'upi' },
      card: { components: ['card'], defaultPaymentMethod: 'card' },
      netbanking: { components: ['netbanking'], defaultPaymentMethod: 'nb' },
    };

    return { ...options, ...channelHints[channel] };
  }

  // Cashfree Integration (standard hosted checkout via modal)
  async openCashfreePayment(
    paymentData: Record<string, unknown>,
    channel?: CashfreePaymentChannel
  ): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Cashfree can only be used in browser environment'));
        return;
      }

      const paymentSessionId =
        (paymentData.payment_session_id as string | undefined) ||
        (paymentData.paymentSessionId as string | undefined);

      if (!paymentSessionId) {
        reject(new Error('Missing Cashfree payment session id in payment data'));
        return;
      }

      const openCashfreeModal = () => {
        try {
          const cashfreeFactory = (window as unknown as {
            Cashfree?: (config: { mode: 'sandbox' | 'production' }) => {
              checkout: (options: {
                paymentSessionId: string;
                redirectTarget?: string;
              }) => Promise<{
                error?: { message?: string };
                redirect?: boolean;
                paymentDetails?: Record<string, unknown>;
              }>;
            };
          }).Cashfree;

          if (!cashfreeFactory) {
            reject(new Error('Cashfree SDK failed to load'));
            return;
          }

          const cashfree = cashfreeFactory({ mode: this.getCashfreeMode() });

          cashfree
            .checkout(
              this.buildCashfreeCheckoutOptions(
                paymentSessionId,
                channel
              ) as {
                paymentSessionId: string;
                redirectTarget?: string;
              }
            )
            .then((result) => {
              if (result.error) {
                reject(
                  new Error(
                    result.error.message || 'Payment cancelled or failed'
                  )
                );
                return;
              }

              if (result.paymentDetails) {
                resolve({
                  ...result.paymentDetails,
                  payment_session_id: paymentSessionId,
                  cashfree_channel: channel,
                  cf_order_id:
                    (result.paymentDetails.orderId as string | undefined) ||
                    (result.paymentDetails.order_id as string | undefined) ||
                    (paymentData.order_id as string | undefined) ||
                    (paymentData.orderId as string | undefined),
                  cf_payment_id:
                    (result.paymentDetails.paymentId as string | undefined) ||
                    (result.paymentDetails.payment_id as string | undefined),
                });
                return;
              }

              if (result.redirect) {
                reject(
                  new Error(
                    'Payment opened in a new window. Complete payment and return to this site.'
                  )
                );
                return;
              }

              reject(new Error('Payment was not completed'));
            })
            .catch((error) => {
              reject(error instanceof Error ? error : new Error('Cashfree payment failed'));
            });
        } catch (error) {
          console.error('Error opening Cashfree checkout:', error);
          reject(error instanceof Error ? error : new Error('Cashfree payment failed'));
        }
      };

      const existingScript = document.querySelector(
        'script[src="https://sdk.cashfree.com/js/v3/cashfree.js"]'
      );

      if (
        (window as unknown as { Cashfree?: unknown }).Cashfree &&
        existingScript
      ) {
        openCashfreeModal();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
      script.onload = () => {
        openCashfreeModal();
      };
      script.onerror = () => {
        console.error('Failed to load Cashfree script');
        reject(new Error('Failed to load Cashfree script'));
      };
      document.head.appendChild(script);
    });
  }

  // PayPal Integration
  async openPayPalPayment(paymentData: {
    amount: number;
    currency: string;
    description: string;
    approval_url: string;
  }): Promise<{
    paymentId: string;
    status: string;
  }> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('PayPal can only be used in browser environment'));
        return;
      }

      // Get the approval URL from PayPal response
      const approvalUrl = paymentData.approval_url;
      if (!approvalUrl) {
        reject(new Error('No approval URL found in PayPal response'));
        return;
      }

      // For PayPal, we redirect the user directly to the approval URL
      // PayPal will handle the payment flow and redirect back to our success/failure URLs
      // The backend should be configured with proper success/failure URLs
      window.location.href = approvalUrl;
      
      // Since we're redirecting, we don't need to resolve here
      // The payment completion will be handled by the backend callback URLs
    });
  }

  // PayPal Verification
  // Note: This can work without authentication for PayPal callbacks from app
  async verifyPayPalPayment(token: string, payerId: string, requireAuth: boolean = false): Promise<{
    success: boolean;
    transactionId: string;
    status: string;
  }> {
    const url = `${this.baseURL}/payment/verify-paypal`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(requireAuth),
        body: JSON.stringify({
          token,
          payerId
        })
      });

      if (!response.ok) {
        const errorData: ErrorResponse = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('PayPal Verification API Error:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const paymentService = new PaymentService();
export default paymentService;
